import { useEffect, useState, type FormEvent } from "react";
import { useLantern, useSession } from "../hooks";
import type { MFAStatus, Passkey } from "../sync/types";
import { Badge, Card, Field, Form } from "./ui";
import { locale, t, tn } from "../i18n";
import { cancelled, creationOptions, credentialJSON, passkeysHere } from "../webauthn";

// SecurityCards are how one signs in, in Settings: passkeys, two-step
// verification with an authenticator app, and its recovery codes.
export function SecurityCards() {
  const session = useSession();
  const [st, setSt] = useState<MFAStatus | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState("");
  const load = () => session.api.mfa().then((r) => setSt(r.mfa), (err) => setError(err instanceof Error ? err.message : String(err)));
  useEffect(() => {
    void load();
  }, [session]);

  if (error) return <p className="error">{error}</p>;
  if (!st) return <p className="muted">{t("Loading…")}</p>;
  if (!st.has_password) {
    return (
      <Card title={t("Two-step verification")}>
        <p className="muted">{t("You sign in with single sign-on, so your identity provider asks for any second step.")}</p>
      </Card>
    );
  }
  return (
    <>
      {codes && <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />}
      <PasskeysCard />
      <TOTPCard
        on={st.totp}
        onChanged={(fresh) => {
          if (fresh) setCodes(fresh);
          void load();
        }}
      />
      {st.totp && (
        <RecoveryCard
          left={st.recovery_codes}
          onNew={(fresh) => {
            setCodes(fresh);
            void load();
          }}
        />
      )}
    </>
  );
}

// groups4 writes a secret in groups of four, to read and type.
const groups4 = (s: string) => s.match(/.{1,4}/g)?.join(" ") ?? s;

function TOTPCard({ on, onChanged }: { on: boolean; onChanged: (codes?: string[]) => void }) {
  const session = useSession();
  const [password, setPassword] = useState("");
  const [setup, setSetup] = useState<{ secret: string; qr: string } | null>(null);
  const [code, setCode] = useState("");
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const failed = (err: unknown) => setError(err instanceof Error ? err.message : String(err));

  async function start(e: FormEvent) {
    e.preventDefault();
    setError("");
    try {
      const r = await session.api.startTOTP(password);
      setSetup(r);
      setPassword("");
    } catch (err) {
      failed(err);
    }
  }
  async function confirm(e: FormEvent) {
    e.preventDefault();
    setError("");
    try {
      const r = await session.api.confirmTOTP(code);
      setSetup(null);
      setAsking(false);
      setCode("");
      setDone(t("Two-step verification is on. Your other devices are signed out."));
      onChanged(r.recovery_codes);
    } catch (err) {
      failed(err);
    }
  }
  async function turnOff(e: FormEvent) {
    e.preventDefault();
    setError("");
    try {
      await session.api.disableTOTP(code);
      setCode("");
      setDone(t("Two-step verification is off. Your other devices are signed out."));
      onChanged();
    } catch (err) {
      failed(err);
    }
  }

  return (
    <Card
      title={t("Two-step verification")}
      description={t("After your password, sign in with a code from an authenticator app on your phone, such as Google Authenticator, Microsoft Authenticator or 1Password.")}
      actions={on ? <Badge tone="ok">{t("On")}</Badge> : <Badge>{t("Off")}</Badge>}
    >
      {done && (
        <p className="notice" role="status">
          {done}
        </p>
      )}
      {on ? (
        <Form onSubmit={turnOff} aria-label={t("Turn off two-step verification")}>
          <Field label={t("Code from your app, or a recovery code")} hint={t("Turning it off needs the factor itself.")}>
            <input value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" autoCapitalize="none" spellCheck={false} required />
          </Field>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <div className="form-actions">
            <button type="submit" className="danger">
              {t("Turn off")}
            </button>
          </div>
        </Form>
      ) : setup ? (
        <Form onSubmit={confirm} className="totp-setup" aria-label={t("Set up an authenticator app")}>
          <ol className="totp-steps">
            <li>
              {t("Scan this code with your authenticator app.")}
              <img className="totp-qr" src={setup.qr} alt={t("QR code for your authenticator app")} width={200} height={200} />
            </li>
            <li>
              {t("Or type this key into it:")} <code className="totp-secret">{groups4(setup.secret)}</code>
            </li>
            <li>
              <Field label={t("The 6-digit code it shows")}>
                <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={7} autoFocus required />
              </Field>
            </li>
          </ol>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <div className="form-actions">
            <button type="button" className="ghost" onClick={() => setSetup(null)}>
              {t("Cancel")}
            </button>
            <button type="submit">{t("Turn on")}</button>
          </div>
        </Form>
      ) : asking ? (
        <Form onSubmit={start} aria-label={t("Set up an authenticator app")}>
          <Field label={t("Your password")} hint={t("To be sure it is you.")}>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" autoFocus required />
          </Field>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <div className="form-actions">
            <button type="button" className="ghost" onClick={() => setAsking(false)}>
              {t("Cancel")}
            </button>
            <button type="submit">{t("Continue")}</button>
          </div>
        </Form>
      ) : (
        <div className="form-actions">
          <button onClick={() => setAsking(true)}>{t("Set up an authenticator app")}</button>
        </div>
      )}
    </Card>
  );
}

function RecoveryCard({ left, onNew }: { left: number; onNew: (codes: string[]) => void }) {
  const session = useSession();
  const [code, setCode] = useState("");
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState("");

  async function renew(e: FormEvent) {
    e.preventDefault();
    setError("");
    try {
      const r = await session.api.renewRecoveryCodes(code);
      setCode("");
      setAsking(false);
      onNew(r.recovery_codes);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <Card
      title={t("Recovery codes")}
      description={t("If you lose your phone, sign in with one of these instead of a code. Each works once.")}
      actions={<Badge tone={left <= 2 ? "warn" : "neutral"}>{tn(left, "{n} left", "{n} left")}</Badge>}
    >
      {asking ? (
        <Form onSubmit={renew} aria-label={t("New recovery codes")}>
          <Field label={t("Code from your app, or a recovery code")} hint={t("New codes replace all the old ones, and your other devices are signed out.")}>
            <input value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" autoCapitalize="none" spellCheck={false} autoFocus required />
          </Field>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <div className="form-actions">
            <button type="button" className="ghost" onClick={() => setAsking(false)}>
              {t("Cancel")}
            </button>
            <button type="submit">{t("Make new codes")}</button>
          </div>
        </Form>
      ) : (
        <div className="form-actions">
          <button onClick={() => setAsking(true)}>{t("Make new codes")}</button>
        </div>
      )}
    </Card>
  );
}

// RecoveryCodes shows new recovery codes, this once, to copy or save.
function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const state = useLantern();
  const [copied, setCopied] = useState(false);
  const text = codes.join("\n") + "\n";
  const [file, setFile] = useState("");
  useEffect(() => {
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    setFile(url);
    return () => URL.revokeObjectURL(url);
  }, [text]);
  return (
    <Card title={t("Your recovery codes")} className="recovery-codes" description={t("Keep them somewhere safe, apart from your phone. They are not shown again.")}>
      <ul className="recovery-list" aria-label={t("Recovery codes")}>
        {codes.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ul>
      <div className="form-actions">
        <button type="button" onClick={() => void navigator.clipboard.writeText(text).then(() => setCopied(true), () => setCopied(false))} aria-live="polite">
          {copied ? t("Copied") : t("Copy")}
        </button>
        <a className="btn" href={file} download={`lantern-recovery-codes-${(state.me?.handle ?? "me").replace(/[^a-z0-9._-]/g, "")}.txt`}>
          {t("Download")}
        </a>
        <button type="button" onClick={onDone}>
          {t("I have saved them")}
        </button>
      </div>
    </Card>
  );
}

// PasskeysCard lists one's passkeys, and adds, names and removes them.
function PasskeysCard() {
  const session = useSession();
  const [list, setList] = useState<Passkey[] | null>(null);
  const [on, setOn] = useState(true);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);
  const failed = (err: unknown) => setError(err instanceof Error ? err.message : String(err));
  const load = () => session.api.passkeys().then((r) => setList(r.passkeys), failed);
  useEffect(() => {
    void load();
    session.api.request<{ passkeys?: { origin: string } }>("GET", "/auth/methods").then((m) => setOn(passkeysHere(m)), () => setOn(false));
  }, [session]);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    setDone("");
    try {
      const begun = await session.api.beginPasskey(password, name);
      const made = (await navigator.credentials.create({ publicKey: creationOptions(begun.options) })) as PublicKeyCredential | null;
      if (!made) return;
      const { passkey } = await session.api.finishPasskey(begun.pending, credentialJSON(made));
      setAdding(false);
      setName("");
      setPassword("");
      setDone(t("{name} is added. Next time, sign in with it alone.", { name: passkey.name }));
      await load();
    } catch (err) {
      if (cancelled(err)) return;
      // The browser's own errors (one added already, a wrong address) are
      // not in Lantern's language.
      if (err instanceof DOMException) setError(err.name === "InvalidStateError" ? t("This device has a passkey here already.") : t("Your passkey could not be used here."));
      else failed(err);
    } finally {
      setBusy(false);
    }
  }
  async function rename(k: Passkey) {
    const next = window.prompt(t("A name for this passkey"), k.name);
    if (next === null || next.trim() === k.name) return;
    setError("");
    await session.api.renamePasskey(k.id, next).then(load, failed);
  }
  async function remove(k: Passkey) {
    if (!window.confirm(t("Remove {name}? It no longer signs you in.", { name: k.name }))) return;
    setError("");
    await session.api.removePasskey(k.id).then(load, failed);
  }
  const date = (d: string) => new Date(d).toLocaleDateString(locale, { dateStyle: "medium" });

  return (
    <Card
      title={t("Passkeys")}
      description={t("Sign in with your fingerprint, face or device PIN instead of your password and a code. A passkey works only for this server, so it cannot be phished.")}
      actions={list && list.length > 0 && <Badge tone="ok">{tn(list.length, "{n} passkey", "{n} passkeys")}</Badge>}
    >
      {done && (
        <p className="notice" role="status">
          {done}
        </p>
      )}
      {!on ? (
        <p className="muted">{t("Passkeys are not available here: this server or this browser does not take them.")}</p>
      ) : (
        <>
          {list && list.length > 0 && (
            <ul className="rows passkey-list" aria-label={t("Passkeys")}>
              {list.map((k) => (
                <li key={k.id}>
                  <span className="row-main">
                    <span className="row-title">{k.name}</span>
                    <span className="row-meta">
                      {t("added {date}", { date: date(k.created_at) })}
                      {" · "}
                      {k.last_used_at ? t("last used {date}", { date: date(k.last_used_at) }) : t("not used yet")}
                    </span>
                  </span>
                  <span className="row-actions">
                    <button className="small ghost" onClick={() => void rename(k)} aria-label={t("Rename {name}", { name: k.name })}>
                      {t("Rename")}
                    </button>
                    <button className="small ghost danger" onClick={() => void remove(k)} aria-label={t("Remove {name}", { name: k.name })}>
                      {t("Remove")}
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {adding ? (
            <Form onSubmit={add} aria-label={t("Add a passkey")} className="passkey-add">
              <div className="inline-fields">
                <Field label={t("Name")} hint={t("Which device or password manager it is in.")}>
                  <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder={t("Work laptop")} autoFocus />
                </Field>
                <Field label={t("Your password")} hint={t("To be sure it is you.")}>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
                </Field>
              </div>
              {error && (
                <p className="error" role="alert">
                  {error}
                </p>
              )}
              <div className="form-actions">
                <button type="button" className="ghost" onClick={() => setAdding(false)}>
                  {t("Cancel")}
                </button>
                <button type="submit" disabled={busy}>
                  {t("Continue")}
                </button>
              </div>
            </Form>
          ) : (
            <>
              {error && (
                <p className="error" role="alert">
                  {error}
                </p>
              )}
              <div className="form-actions">
                <button onClick={() => setAdding(true)}>{t("Add a passkey")}</button>
              </div>
            </>
          )}
        </>
      )}
    </Card>
  );
}
